import React, { useState, useEffect } from 'react';
import { parseDate, formatDateString, formatTimeString } from '../../../utils/dateUtils';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Card,
  CardContent,
  Divider,
  Grid,
  TextField,
  InputAdornment,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tabs,
  Tab,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Badge
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import AppService from '../../../Configurations/Appservice';
// Use AppService instead of axios for proper authentication

// Status chip component with compact design
const StatusChip = ({ status }) => {
  let color = 'default';
  
  switch (status?.toUpperCase()) {
    case 'COMPLETED':
      color = 'success';
      break;
    case 'IN_PROGRESS':
      color = 'warning';
      break;
    case 'CANCELLED':
    case 'REJECTED':
      color = 'error';
      break;
    case 'CREATED':
    case 'ASSIGNED':
      color = 'info';
      break;
    case 'PENDING':
      color = 'default';
      break;
    default:
      color = 'default';
  }
  
  return (
    <Chip 
      label={status || 'Unknown'} 
      color={color} 
      size="small" 
      sx={{ height: 24, fontSize: '0.75rem' }}
    />
  );
};

// Workflow chip component with compact design
const WorkflowChip = ({ workflowName, workflowId }) => {
  return workflowName ? (
    <Tooltip title={`Workflow ID: ${workflowId}`}>
      <Chip 
        label={workflowName} 
        color="primary" 
        size="small" 
        variant="outlined"
        sx={{ height: 24, fontSize: '0.75rem', maxWidth: 150 }}
      />
    </Tooltip>
  ) : (
    <Chip 
      label="Standalone" 
      color="default" 
      size="small" 
      variant="outlined"
      sx={{ height: 24, fontSize: '0.75rem' }}
    />
  );
};

const CustomerTicketList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [workflowRequests, setWorkflowRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Get user info from localStorage with more robust error handling
  const getUserInfo = () => {
    try {
      const authInfo = localStorage.getItem('authInfo');
      if (authInfo) {
        const parsed = JSON.parse(authInfo);
        console.log('User auth info:', parsed);
        
        // Check if this is a customer user
        const userType = parsed?.principalInfo?.userType || parsed?.user?.userType || parsed?.userType;
        if (userType && userType !== 'CUSTOMER') {
          console.warn(`User type is ${userType}, not CUSTOMER. This view is intended for customer users.`);
        }
        
        return parsed;
      }
    } catch (error) {
      console.error('Error parsing user info:', error);
    }
    return null;
  };
  
  const userInfo = getUserInfo();
  // Handle different possible paths to the organization ID based on your authentication structure
  const customerId = userInfo?.principalInfo?.organisationId || 
                     userInfo?.organisation?.id || 
                     userInfo?.user?.organisationId || 
                     userInfo?.orgId;
                     
  console.log('Customer organization ID:', customerId);
                     
  // For debugging - log the JWT token (without showing it in UI)
  useEffect(() => {
    const token = userInfo?.token;
    if (token) {
      console.log('JWT token is present, length:', token.length);
      // Don't log the actual token for security reasons
    } else {
      console.warn('No JWT token found in user info');
    }
  }, [userInfo]);
                     
  // Log the customer ID for debugging
  useEffect(() => {
    console.log('Customer organization ID:', customerId);
    if (!customerId) {
      console.warn('No customer organization ID found in user info');
    }
  }, [customerId]);

  // Function to handle API errors with fallback
  const handleApiError = (error, fallbackAction, errorMessage) => {
    console.error(errorMessage, error);
    setError(error.message || 'An unexpected error occurred');
    
    // Execute fallback action if provided
    if (fallbackAction) {
      fallbackAction();
    }
  };

  // Function to fetch tickets from the API with robust error handling
  const fetchTickets = async (showRefreshing = false) => {
    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      // Prepare arrays to store our data
      let ticketsData = [];
      let workflowRequestsData = [];
      
      // First, check if we have valid authentication
      if (!userInfo || !userInfo.token) {
        console.error('No authentication token found');
        setError('Authentication required. Please log in again.');
        setLoading(false);
        setRefreshing(false);
        return;
      }
      
      // Then check if we have a valid customer ID
      if (!customerId) {
        console.error('No customer organization ID found');
        setError('Unable to determine your organization. Please contact support.');
        setLoading(false);
        setRefreshing(false);
        return;
      }
      
      console.log('Authenticated user from organization:', customerId);
      
      // First, try to get tickets for the current user (endpoint allowed for all roles)
      try {
        console.log('Fetching my tickets from /v1/ticket/myTickets');
        const ticketsResponse = await AppService.get('/v1/ticket/myTickets');
        console.log('Ticket API response:', ticketsResponse);
        
        // Handle different response formats first
        let allTickets = Array.isArray(ticketsResponse) ? 
          ticketsResponse : 
          (ticketsResponse.content || ticketsResponse.data || []);
          
        console.log('All tickets before filtering:', allTickets);
          
        // Filter tickets for this customer if needed
        // This is a fallback approach using client-side filtering similar to our service mapping approach
        if (customerId) {
          console.log(`Filtering tickets for customer organization: ${customerId}`);
          const customerTickets = allTickets.filter(ticket => {
            const ticketOrgId = ticket.customerOrganisation || 
                               ticket.customerOrganizationId || 
                               ticket.customerId || 
                               ticket.customer_organization_id;
            // Log each ticket's organization for debugging
            console.log(`Ticket ${ticket.id} has org: ${ticketOrgId}, comparing with ${customerId}`);
            return ticketOrgId === customerId;
          });
          console.log(`Found ${customerTickets.length} tickets for this customer organization`);
          allTickets = customerTickets;
        }
        
        // Use the filtered tickets
        ticketsData = allTickets;
        
        // Debug the ticket data
        console.log('Final ticketsData to be displayed:', ticketsData);
        
        // Log the number of tickets for debugging
        console.log(`Retrieved ${ticketsData.length} customer tickets`);
        
        // Normalize ticket data fields
        ticketsData = ticketsData.map(ticket => ({
          ...ticket,
          // Normalize ID fields
          id: ticket.id || ticket.ticketId || ticket.ticket_id,
          ticketNumber: ticket.ticketNumber || ticket.number || ticket.ticket_number || `TK-${ticket.id?.substring(0, 8)}`,
          // Normalize service fields
          serviceName: ticket.serviceName || ticket.service?.name || ticket.service_name || 'Unknown Service',
          serviceId: ticket.serviceId || ticket.service?.id || ticket.service_id,
          // Normalize customer fields
          customerId: ticket.customerId || ticket.customer?.id || ticket.customer_id || customerId,
          customerName: ticket.customerName || ticket.customer?.name || ticket.customer_name || 'Your Organization',
          // Normalize date fields
          createDateTime: ticket.createDateTime || ticket.createdAt || ticket.created_at || new Date().toISOString(),
          // Normalize status field - this is critical for filtering
          status: ticket.status || 'OPEN',
          // Default to not part of workflow (will be updated later if needed)
          isPartOfWorkflow: false
        }));
        
        console.log('Normalized ticket data:', ticketsData);
        
        // Sort tickets by creation date (newest first)
        ticketsData.sort((a, b) => new Date(b.createDateTime || b.createdAt || Date.now()) - new Date(a.createDateTime || a.createdAt || Date.now()));
        
        // Set state with the fetched data
        console.log('Setting tickets state with data:', ticketsData);
        setTickets(ticketsData);
        setWorkflowRequests(workflowRequestsData);
        
        // Also update the filtered tickets for display
        console.log('Setting filteredTickets state with data:', ticketsData);
        setFilteredTickets(ticketsData);
      } catch (ticketError) {
        console.error('Error fetching customer tickets:', ticketError);
        setError('Unable to load your tickets. Please try again later.');
        setTickets([]);
      }
      
      // TEMPORARILY SKIP workflow instances API call due to server-side user authentication issues
      console.log('Skipping workflow instances API call due to server-side authentication issues');
      
      // Set empty workflow requests
      setWorkflowRequests([]);
      
      // Show a message in the console for debugging
      console.info('Note: Workflow data is not being loaded due to backend authentication issues.');
      
      // If we need to simulate workflow data for testing, we could add it here
      // But for now, we'll just use the standalone tickets
      
    } catch (error) {
      console.error('Error in fetchTickets:', error);
      setError('Failed to load ticket data. Please try again later.');
      setTickets([]);
      setWorkflowRequests([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  // Initial data loading
  useEffect(() => {
    console.log('CustomerTicketList mounted with customerId:', customerId);
    
    if (!customerId) {
      console.warn('No customer ID available yet, waiting for user info');
      return;
    }
    
    // Force a small delay to ensure the component is fully mounted
    const timer = setTimeout(() => {
      fetchTickets();
    }, 500); // Increased delay to ensure authentication is loaded
    return () => clearTimeout(timer);
  }, [customerId]); // Re-fetch when customerId changes
  
  // Debug the state of filteredTickets
  useEffect(() => {
    console.log('filteredTickets state updated:', filteredTickets);
  }, [filteredTickets]);
  
  // Handle user authentication issues
  useEffect(() => {
    if (!userInfo) {
      setError('You are not logged in. Please log in to view your tickets.');
      setLoading(false);
      return;
    }
    
    // Check if this is a customer user
    const userType = userInfo?.principalInfo?.userType || userInfo?.user?.userType || userInfo?.userType;
    if (userType && userType !== 'CUSTOMER') {
      console.warn(`User type is ${userType}, not CUSTOMER. This view is intended for customer users.`);
      // We don't show an error here, as admins might need to view this page too
    }
    
    // Check if we have a customer organization ID
    if (!customerId) {
      setError('Your account is not associated with a customer organization. Please contact support.');
      setLoading(false);
    }
  }, [userInfo, customerId]);
  
  // Function to handle refresh button click
  const handleRefresh = () => {
    fetchTickets(true);
  };


  // Function to handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Function to handle view ticket details
  const handleViewTicket = (ticketId) => {
    console.log('View ticket called with ID:', ticketId);
    
    if (!ticketId) {
      console.error('Invalid ticketId provided');
      return;
    }
    
    // Find the ticket in our data
    const selectedTicket = tickets.find(ticket => ticket.id === ticketId);
    
    if (!selectedTicket) {
      console.error('Ticket not found:', ticketId);
      return;
    }
    
    // Navigate to ticket details page
    navigate(`/ticket/ticket-details`, {
      state: {
        ticketId: ticketId,
        ticket: selectedTicket
      }
    });
  };

  // Function to handle view workflow
  const handleViewWorkflow = (requestId) => {
    navigate(`/CST-Workflow/requests/${requestId}`);
  };
  
  // Function to create new ticket
  const handleCreateTicket = () => {
    navigate('/ticket/new-ticket');
  };
  
  // Function to create new workflow request
  const handleCreateWorkflowRequest = () => {
    navigate('/CST-Workflow/requests/new');
  };

  // Filter tickets based on search term and status
  useEffect(() => {
    console.log('Filter effect running with tickets:', tickets);
    if (!tickets || !Array.isArray(tickets)) {
      console.log('No tickets to filter or tickets is not an array');
      return;
    }
    
    const filtered = tickets.filter(ticket => {
      // Check if ticket matches the search term
      const ticketNumber = ticket.ticketNumber?.toString() || '';
      const serviceName = ticket.serviceName || '';
      const status = ticket.status || '';
      
      const matchesSearch = 
        ticketNumber.includes(searchTerm) ||
        serviceName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        status.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Check if ticket matches the status filter
      // Handle case-insensitive status comparison
      const matchesStatus = filterStatus === 'all' || 
                          status.toUpperCase() === filterStatus.toUpperCase() ||
                          (filterStatus === 'PENDING' && status === 'OPEN'); // Treat OPEN as PENDING for filtering
      
      return matchesSearch && matchesStatus;
    });
    
    console.log(`Filtered ${tickets.length} tickets down to ${filtered.length} based on search/filter`);
    setFilteredTickets(filtered);
  }, [tickets, searchTerm, filterStatus]);

  // Get unique statuses for the filter dropdown
  const uniqueStatuses = [...new Set(tickets.map(ticket => {
    // Map OPEN status to PENDING for consistency in the UI
    const status = ticket.status?.toUpperCase();
    return status === 'OPEN' ? 'PENDING' : status;
  }).filter(Boolean))];
  
  // Debug the tickets and filteredTickets arrays
  console.log('Rendering with tickets:', tickets);
  console.log('Rendering with filteredTickets:', filteredTickets);
  
  // Count tickets by status for summary
  const statusCounts = tickets.reduce((counts, ticket) => {
    const status = ticket.status?.toUpperCase() || 'UNKNOWN';
    counts[status] = (counts[status] || 0) + 1;
    return counts;
  }, {});
  
  // Prepare workflow requests for the requests tab
  const filteredWorkflowRequests = workflowRequests.filter(request => {
    const requestTitle = request.requestTitle || request.workflowName || '';
    const workflowName = request.workflowName || request.workflowType || '';
    const status = request.status || '';
    
    return (
      requestTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      workflowName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      status.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '50vh' }}>
        <CircularProgress size={40} thickness={4} />
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Loading your tickets...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h1">
          My Tickets
        </Typography>
        <Box>
          <Button 
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            size="small"
            sx={{ mr: 1 }}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={activeTab === 0 ? handleCreateTicket : handleCreateWorkflowRequest}
            size="small"
          >
            {activeTab === 0 ? 'New Ticket' : 'New Request'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Status summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Total Tickets
              </Typography>
              <Typography variant="h4">
                {tickets.length}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                <AssignmentIcon fontSize="small" color="primary" sx={{ mr: 0.5 }} />
                <Typography variant="body2" color="textSecondary">
                  {tickets.filter(t => t.isPartOfWorkflow).length} in workflows
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                In Progress
              </Typography>
              <Typography variant="h4" color="warning.main">
                {statusCounts['IN_PROGRESS'] || 0}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                <InfoIcon fontSize="small" color="warning" sx={{ mr: 0.5 }} />
                <Typography variant="body2" color="textSecondary">
                  Being processed
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Completed
              </Typography>
              <Typography variant="h4" color="success.main">
                {statusCounts['COMPLETED'] || 0}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 0.5 }} />
                <Typography variant="body2" color="textSecondary">
                  Successfully processed
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Pending
              </Typography>
              <Typography variant="h4" color="info.main">
                {statusCounts['PENDING'] || 0}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                <InfoIcon fontSize="small" color="info" sx={{ mr: 0.5 }} />
                <Typography variant="body2" color="textSecondary">
                  Awaiting processing
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2">My Tickets</Typography>
                  <Badge badgeContent={tickets.length} color="primary" sx={{ ml: 1 }} />
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2">My Requests</Typography>
                  <Badge badgeContent={workflowRequests.length} color="secondary" sx={{ ml: 1 }} />
                </Box>
              }
            />
          </Tabs>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search..."
            variant="outlined"
            size="small"
            sx={{ flexGrow: 1, minWidth: '200px', maxWidth: '400px' }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          
          {activeTab === 0 && (
            <FormControl size="small" sx={{ minWidth: '150px' }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                {uniqueStatuses.map(status => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {activeTab === 0 ? (
          /* Tickets Tab */
          <>
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" color="textSecondary">
                {filteredTickets.length} tickets found
              </Typography>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 600 }}>Ticket ID</TableCell>
                    <TableCell sx={{ color: 'white', fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 600 }}>Service</TableCell>
                    <TableCell sx={{ color: 'white', fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 600 }}>Created Date</TableCell>
                    <TableCell sx={{ color: 'white', fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ color: 'white', fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 600 }}>Workflow</TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Debug the filteredTickets length */}
                  {console.log('filteredTickets.length in render:', filteredTickets.length)}
                  {filteredTickets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Box sx={{ py: 2 }}>
                          <Typography variant="body2" color="textSecondary">
                            No tickets found matching your criteria.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    /* Map over the filteredTickets array */
                    filteredTickets.map((ticket) => {
                      console.log('Rendering ticket:', ticket);
                      // Handle different API response formats
                      const ticketId = ticket.id;
                      const ticketNumber = ticket.ticketNumber || `TK-${ticketId.substring(0, 5)}`;
                      const serviceName = ticket.serviceName || ticket.service?.name || 'Unknown Service';
                      const createdDate = ticket.createDateTime || ticket.createdAt || new Date().toISOString();
                      // Make sure we have a status value and map OPEN to PENDING for display
                      let status = ticket.status || 'OPEN';
                      
                      // For display purposes, treat OPEN as PENDING
                      if (status === 'OPEN') {
                        status = 'PENDING';
                      }
                      
                      console.log(`Rendering ticket ${ticketId} with status: ${status}`);
                      
                      return (
                        <TableRow key={ticketId} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {ticketNumber}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {ticketId.substring(0, 8)}...
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {serviceName}
                            </Typography>
                            {ticket.stepName && (
                              <Typography variant="caption" color="textSecondary">
                                Step: {ticket.stepName}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDateString(createdDate)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {formatTimeString(createdDate)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusChip status={status} />
                          </TableCell>
                          <TableCell>
                            <WorkflowChip 
                              workflowName={ticket.workflowName} 
                              workflowId={ticket.workflowId} 
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View Ticket Details">
                              <IconButton
                                color="primary"
                                onClick={() => handleViewTicket(ticketId)}
                                size="small"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            {ticket.isPartOfWorkflow && ticket.requestId && (
                              <Tooltip title="View Workflow Request">
                                <IconButton
                                  color="secondary"
                                  onClick={() => handleViewWorkflow(ticket.requestId)}
                                  size="small"
                                >
                                  <AssignmentIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          /* Workflow Requests Tab */
          <>
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" color="textSecondary">
                {filteredWorkflowRequests.length} workflow requests found
              </Typography>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Request ID</TableCell>
                    <TableCell>Workflow Type</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Tickets</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredWorkflowRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Box sx={{ py: 2 }}>
                          <Typography variant="body2" color="textSecondary">
                            No workflow requests found matching your criteria.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredWorkflowRequests.map((request) => {
                      // Handle different API response formats
                      const requestId = request.id;
                      const workflowName = request.workflowName || request.workflowType || 'Unknown Workflow';
                      const createdDate = request.createDateTime || request.createdAt || new Date().toISOString();
                      const status = request.status || 'PENDING';
                      const ticketCount = request.tickets?.length || 0;
                      
                      return (
                        <TableRow key={requestId} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {requestId.substring(0, 8)}...
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {workflowName}
                            </Typography>
                            {request.variant && (
                              <Typography variant="caption" color="textSecondary">
                                Variant: {request.variant}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDateString(createdDate)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {formatTimeString(createdDate)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusChip status={status} />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={`${ticketCount} tickets`} 
                              color="primary" 
                              size="small"
                              variant="outlined"
                              sx={{ height: 24, fontSize: '0.75rem' }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View Request Details">
                              <IconButton
                                color="primary"
                                onClick={() => handleViewWorkflow(requestId)}
                                size="small"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default CustomerTicketList;
